/**
 * Searchable activity combobox — supports 100+ activities without overwhelming the user.
 * Filters activities by typed query; shows "All activities" when empty.
 */
import React, { useState, useRef, useEffect } from 'react';

export default function ActivitySelector({
  activities = [],
  value = '',
  onChange,
  placeholder = 'All activities',
  className = '',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = React.useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return activities.slice(0, 80);
    return activities.filter((a) => (a || '').toLowerCase().includes(q)).slice(0, 80);
  }, [activities, query]);

  const selectedLabel = value || placeholder;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query, filtered.length]);

  useEffect(() => {
    if (open && listRef.current && highlightIndex >= 0 && highlightIndex < filtered.length) {
      const el = listRef.current.children[highlightIndex];
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [open, highlightIndex, filtered.length]);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % Math.max(1, filtered.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + filtered.length) % Math.max(1, filtered.length));
      return;
    }
    if (e.key === 'Enter' && filtered[highlightIndex]) {
      e.preventDefault();
      onChange(filtered[highlightIndex]);
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div
        className={`flex items-center gap-2 bg-white border rounded-lg min-w-[200px] max-w-[280px] ${
          open ? 'ring-2 ring-indigo-500/30 border-indigo-400' : 'border-slate-200 hover:border-slate-300'
        } transition-colors`}
      >
        <span className="pl-3 text-slate-400" aria-hidden>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          value={open ? query : selectedLabel}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 py-2 pr-3 text-sm text-slate-700 placeholder-slate-400 bg-transparent border-0 focus:outline-none focus:ring-0"
          aria-label="Search activities"
          aria-autocomplete="list"
          aria-controls="activity-list"
          aria-activedescendant={open && filtered[highlightIndex] ? `activity-${highlightIndex}` : undefined}
        />
        <button
          type="button"
          onClick={() => {
            setOpen(!open);
            if (!open) {
              setQuery('');
              inputRef.current?.focus();
            }
          }}
          className="p-2 text-slate-400 hover:text-slate-600"
          aria-label={open ? 'Close' : 'Open'}
        >
          <svg
            className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {open && (
        <ul
          ref={listRef}
          id="activity-list"
          role="listbox"
          className="absolute z-50 mt-1 w-full max-w-[280px] max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1"
        >
          <li
            role="option"
            aria-selected={!value}
            onClick={() => {
              onChange('');
              setOpen(false);
              setQuery('');
            }}
            className={`px-3 py-2 text-sm cursor-pointer ${
              !value ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            {placeholder}
          </li>
          {filtered.map((activity, i) => (
            <li
              key={activity}
              id={`activity-${i}`}
              role="option"
              aria-selected={value === activity}
              onClick={() => {
                onChange(activity);
                setOpen(false);
                setQuery('');
              }}
              onMouseEnter={() => setHighlightIndex(i)}
              className={`px-3 py-2 text-sm cursor-pointer truncate ${
                value === activity ? 'bg-indigo-50 text-indigo-700 font-medium' : ''
              } ${highlightIndex === i ? 'bg-slate-50' : ''} hover:bg-slate-50`}
            >
              {activity}
            </li>
          ))}
          {filtered.length === 0 && query && (
            <li className="px-3 py-4 text-sm text-slate-500 text-center">No matching activities</li>
          )}
        </ul>
      )}
    </div>
  );
}
